import React, { useState, useMemo, useEffect } from 'react';
import { jsPDF } from 'jspdf';
import { base44 } from '@/api/base44Client';
import {
  Scale, CheckCircle2, AlertTriangle, Calendar, Lock, Upload, Loader2,
  FileText, Save, History, Download, Trash2,
} from 'lucide-react';
import { useToast } from '@/components/ui/use-toast';

// Sage reconciliation panel. Lets the admin lock the invoice audit view to a
// cut-off date, type in the Sage 50 net + VAT + gross figures for the same
// period, attach the Sage report PDF, and instantly see whether Base44
// matches Sage to the penny. Once checked and approved, the reconciliation is
// saved to the SageReconciliation entity and listed below for audit history.

const fmt = (n) => `£${(Number(n) || 0).toFixed(2)}`;
const todayISO = () => new Date().toISOString().slice(0, 10);

// Parse ISO (yyyy-mm-dd), UK (dd/mm/yyyy) or full timestamp strings into a
// real Date — mirrors InvoicesTab.parseAnyDate so legacy B2B po_date values
// stored in dd/mm/yyyy form are still counted.
function parseAnyDate(s) {
  if (!s) return null;
  const str = String(s).trim();
  let m = str.match(/^(\d{4})-(\d{2})-(\d{2})(?:T(.+))?$/);
  if (m) return new Date(str.length === 10 ? str + 'T00:00:00' : str);
  m = str.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (m) return new Date(Date.UTC(Number(m[3]), Number(m[2]) - 1, Number(m[1])));
  const parsed = new Date(str);
  return isNaN(parsed.getTime()) ? null : parsed;
}

export default function SageReconcilePanel() {
  const { toast } = useToast();
  const [reconcileDate, setReconcileDate] = useState('');
  const [sageNet, setSageNet] = useState('');
  const [sageVat, setSageVat] = useState('');
  const [sageGross, setSageGross] = useState('');
  const [reportUrl, setReportUrl] = useState('');
  const [reportName, setReportName] = useState('');
  const [uploading, setUploading] = useState(false);
  const [extracting, setExtracting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [notes, setNotes] = useState('');
  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(true);
  const [b2bOrders, setB2bOrders] = useState([]);
  const [retailOrders, setRetailOrders] = useState([]);
  const [creditNotes, setCreditNotes] = useState([]);
  const [loadingDocs, setLoadingDocs] = useState(true);

  // Fetch every audited sales document so the panel can stand alone in its
  // own tab without relying on a parent to pass data down.
  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const [b2b, retail, credits] = await Promise.all([
          base44.entities.B2BOrder.list('-created_date', 500),
          base44.entities.Order.list('-created_date', 500),
          base44.entities.CreditNote.list('-created_date', 500),
        ]);
        if (!active) return;
        setB2bOrders(b2b || []);
        setRetailOrders(retail || []);
        setCreditNotes(credits || []);
      } catch (e) {
        console.warn('[SageReconcilePanel] docs load failed', e);
      }
      if (active) setLoadingDocs(false);
    })();
    return () => { active = false; };
  }, []);

  const loadHistory = async () => {
    setLoadingHistory(true);
    try {
      const rows = await base44.entities.SageReconciliation.list('-approved_at', 50);
      setHistory(rows || []);
    } catch (e) {
      console.warn('[SageReconcilePanel] history load failed', e);
    }
    setLoadingHistory(false);
  };
  useEffect(() => { loadHistory(); }, []);

  // Base44 totals for every audited document dated on or before the cut-off.
  const base44Totals = useMemo(() => {
    if (!reconcileDate) return null;
    const cutoff = new Date(reconcileDate + 'T23:59:59');
    let gross = 0;
    let count = 0;

    for (const o of b2bOrders) {
      if (!o.invoice_url && !o.invoice_number) continue;
      const date = parseAnyDate(o.sage_invoice_date) || parseAnyDate(o.po_date) || parseAnyDate(o.created_date);
      if (!date || date > cutoff) continue;
      gross += Number(o.total_inc_vat) || 0;
      count++;
    }
    for (const o of retailOrders) {
      if (!o.invoice_url && !o.invoice_number) continue;
      const date = parseAnyDate(o.sage_invoice_date) || parseAnyDate(o.created_date);
      if (!date || date > cutoff) continue;
      gross += Number(o.amount) || 0;
      count++;
    }
    for (const c of creditNotes) {
      const date = parseAnyDate(c.created_date);
      if (!date || date > cutoff) continue;
      gross -= Number(c.amount) || 0;
      count++;
    }

    const g = Math.round(gross * 100) / 100;
    const vat = Math.ceil((g / 6) * 100) / 100;
    const net = Math.round((g - vat) * 100) / 100;
    return { net, vat, gross: g, count };
  }, [reconcileDate, b2bOrders, retailOrders, creditNotes]);

  const sage = {
    net: parseFloat(sageNet) || 0,
    vat: parseFloat(sageVat) || 0,
    gross: parseFloat(sageGross) || 0,
  };

  const hasSage = sageNet !== '' && sageVat !== '' && sageGross !== '';
  const hasDate = Boolean(reconcileDate);

  const diff = hasSage && base44Totals
    ? {
        net: Math.round((base44Totals.net - sage.net) * 100) / 100,
        vat: Math.round((base44Totals.vat - sage.vat) * 100) / 100,
        gross: Math.round((base44Totals.gross - sage.gross) * 100) / 100,
      }
    : null;

  const balanced = diff && diff.net === 0 && diff.vat === 0 && diff.gross === 0;
  const canSave = hasDate && base44Totals && base44Totals.count > 0 && hasSage && !saving;
  // Most recently approved reconciliation — shown as a reference so the
  // current Sage entry can be compared against the last locked figures.
  const lastRecon = history[0] || null;

  const onUploadReport = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!file.type.includes('pdf')) {
      toast({ title: 'PDF only', description: 'Please upload the Sage report as a PDF', variant: 'destructive' });
      return;
    }
    setUploading(true);
    try {
      const res = await base44.integrations.Core.UploadFile({ file });
      setReportUrl(res.file_url);
      setReportName(file.name);
      toast({ title: 'Sage report attached', description: file.name });
      // Autofill: read the figures + period end date straight out of the PDF
      setExtracting(true);
      try {
        const out = await base44.integrations.Core.InvokeLLM({
          prompt: 'This is a Sage 50 sales report. Extract the period end / cut-off date and the total Net, VAT and Gross (inc. VAT) sales figures in GBP. Return the date as YYYY-MM-DD and the amounts as numbers. If a value is not present, return 0 for amounts and an empty string for the date.',
          file_urls: [res.file_url],
          response_json_schema: {
            type: 'object',
            properties: {
              reconcile_date: { type: 'string', description: 'Period end / cut-off date as YYYY-MM-DD, or empty string' },
              sage_net: { type: 'number', description: 'Total net sales in GBP' },
              sage_vat: { type: 'number', description: 'Total VAT in GBP' },
              sage_gross: { type: 'number', description: 'Total gross (inc. VAT) sales in GBP' },
            },
          },
        });
        if (out?.reconcile_date) setReconcileDate(out.reconcile_date);
        if (typeof out?.sage_net === 'number') setSageNet(String(out.sage_net));
        if (typeof out?.sage_vat === 'number') setSageVat(String(out.sage_vat));
        if (typeof out?.sage_gross === 'number') setSageGross(String(out.sage_gross));
        toast({ title: 'Figures autofilled from PDF', description: 'Check the extracted values before approving' });
      } catch (err) {
        console.warn('[SageReconcilePanel] extract failed', err);
        toast({ title: 'Could not autofill from PDF', description: 'Enter the figures manually', variant: 'destructive' });
      }
      setExtracting(false);
    } catch (err) {
      toast({ title: 'Upload failed', description: err.message, variant: 'destructive' });
    }
    setUploading(false);
  };

  const approveAndSave = async () => {
    if (!canSave) return;
    // Double-check: show the full figures and ask the admin to confirm they
    // are happy everything is balanced before locking the reconciliation.
    const summary =
      `Please confirm this reconciliation is correct:\n\n` +
      `Cut-off date: ${reconcileDate}\n` +
      `Documents counted: ${base44Totals.count}\n\n` +
      `MRIWS  — Net: ${fmt(base44Totals.net)}  VAT: ${fmt(base44Totals.vat)}  Gross: ${fmt(base44Totals.gross)}\n` +
      `Sage   — Net: ${fmt(sage.net)}  VAT: ${fmt(sage.vat)}  Gross: ${fmt(sage.gross)}\n\n` +
      `Status: ${balanced ? 'BALANCED ✓' : 'OUT OF BALANCE ✗'}\n` +
      (balanced ? '' : `Differences — Net: ${fmt(diff.net)}  VAT: ${fmt(diff.vat)}  Gross: ${fmt(diff.gross)}\n`) +
      `\nAre you happy everything is balanced?`;
    if (!window.confirm(summary)) return;
    setSaving(true);
    try {
      const me = await base44.auth.me().catch(() => null);
      const approvedAt = new Date().toISOString();
      const record = await base44.entities.SageReconciliation.create({
        reconcile_date: reconcileDate,
        base44_net: base44Totals.net,
        base44_vat: base44Totals.vat,
        base44_gross: base44Totals.gross,
        sage_net: sage.net,
        sage_vat: sage.vat,
        sage_gross: sage.gross,
        diff_net: diff.net,
        diff_vat: diff.vat,
        diff_gross: diff.gross,
        balanced: !!balanced,
        document_count: base44Totals.count,
        sage_report_url: reportUrl || '',
        notes: notes || '',
        approved_by: me?.email || '',
        approved_at: approvedAt,
      });
      // Generate an audit PDF with the reconciliation figures + details.
      downloadReconciliationPdf({
        id: record?.id,
        reconcile_date: reconcileDate,
        base44_net: base44Totals.net,
        base44_vat: base44Totals.vat,
        base44_gross: base44Totals.gross,
        sage_net: sage.net,
        sage_vat: sage.vat,
        sage_gross: sage.gross,
        diff_net: diff.net,
        diff_vat: diff.vat,
        diff_gross: diff.gross,
        balanced: !!balanced,
        document_count: base44Totals.count,
        notes: notes || '',
        approved_by: me?.email || '',
        approved_at: approvedAt,
        last_recon: lastRecon,
      });
      toast({
        title: balanced ? 'Reconciliation approved & saved' : 'Reconciliation saved (out of balance)',
        description: `Cut-off ${reconcileDate} · ${base44Totals.count} documents · audit PDF downloaded`,
      });
      // Reset the form
      setReconcileDate(''); setSageNet(''); setSageVat(''); setSageGross('');
      setReportUrl(''); setReportName(''); setNotes('');
      loadHistory();
    } catch (err) {
      toast({ title: 'Save failed', description: err.message, variant: 'destructive' });
    }
    setSaving(false);
  };

  const deleteHistory = async (id) => {
    if (!window.confirm('Delete this saved reconciliation? This cannot be undone.')) return;
    try {
      await base44.entities.SageReconciliation.delete(id);
      loadHistory();
    } catch (e) {
      toast({ title: 'Delete failed', description: e.message, variant: 'destructive' });
    }
  };

  return (
    <div className="space-y-4">
      <div className="border border-white/10 bg-[#111115] p-5 space-y-4">
        <div className="flex items-center gap-2">
          <Scale className="w-4 h-4 text-primary" />
          <p className="text-primary text-[11px] font-bold tracking-widest">RECONCILE TO SAGE 50</p>
          <span className="text-white/30 text-[10px] ml-auto">Lock the audit view to a cut-off date, attach the Sage report, and check MRIWS matches Sage to the penny</span>
        </div>

        <div className="bg-white/[0.02] border border-white/5 px-3 py-2 mb-1">
          <p className="text-white/50 text-xs leading-relaxed">
            <span className="text-white/70 font-bold">Sage 50 verification:</span> To verify the import, run the{' '}
            <span className="text-white/70">Day Books: Customer Transactions</span> report.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {/* Cut-off date */}
          <div>
            <label className="text-white/40 text-[10px] font-bold tracking-widest mb-1.5 flex items-center gap-1.5">
              <Calendar className="w-3 h-3" /> RECONCILE TO DATE
            </label>
            <input
              type="date"
              value={reconcileDate}
              onChange={(e) => setReconcileDate(e.target.value)}
              className="seo-input"
              placeholder="31/07/2026"
            />
            <p className="text-white/30 text-[10px] mt-1">All invoices dated on or before this are counted.</p>
          </div>
          {/* Sage net */}
          <div>
            <label className="text-white/40 text-[10px] font-bold tracking-widest mb-1.5 block">SAGE NET (£)</label>
            <input
              type="number"
              step="0.01"
              value={sageNet}
              onChange={(e) => setSageNet(e.target.value)}
              className="seo-input"
              placeholder="40444.61"
            />
          </div>
          {/* Sage vat */}
          <div>
            <label className="text-white/40 text-[10px] font-bold tracking-widest mb-1.5 block">SAGE VAT (£)</label>
            <input
              type="number"
              step="0.01"
              value={sageVat}
              onChange={(e) => setSageVat(e.target.value)}
              className="seo-input"
              placeholder="8088.93"
            />
          </div>
          {/* Sage gross */}
          <div>
            <label className="text-white/40 text-[10px] font-bold tracking-widest mb-1.5 block">SAGE GROSS (£)</label>
            <input
              type="number"
              step="0.01"
              value={sageGross}
              onChange={(e) => setSageGross(e.target.value)}
              className="seo-input"
              placeholder="48533.54"
            />
          </div>
        </div>

        {/* Sage report PDF upload (drag & drop) */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="text-white/40 text-[10px] font-bold tracking-widest mb-1.5 flex items-center gap-1.5">
              <FileText className="w-3 h-3" /> SAGE REPORT (PDF)
            </label>
            <div
              onDragOver={(e) => { e.preventDefault(); e.currentTarget.classList.add('border-primary', 'bg-primary/[0.06]'); }}
              onDragLeave={(e) => { e.preventDefault(); e.currentTarget.classList.remove('border-primary', 'bg-primary/[0.06]'); }}
              onDrop={(e) => {
                e.preventDefault();
                e.currentTarget.classList.remove('border-primary', 'bg-primary/[0.06]');
                const file = e.dataTransfer.files?.[0];
                if (file) onUploadReport({ target: { files: [file], value: '' } });
              }}
              className={`relative border-2 border-dashed ${reportUrl ? 'border-green-400/40 bg-green-400/[0.03]' : 'border-white/15 hover:border-white/30'} transition-colors px-4 py-4 flex items-center gap-3 ${(uploading || extracting) ? 'opacity-60' : ''}`}
            >
              <label className="inline-flex items-center gap-1.5 text-white/70 hover:text-white font-bold text-[10px] tracking-widest cursor-pointer shrink-0">
                {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
                {uploading ? 'UPLOADING…' : 'BROWSE'}
                <input type="file" accept="application/pdf" onChange={onUploadReport} className="hidden" disabled={uploading || extracting} />
              </label>
              <span className="text-white/30 text-[10px]">
                {extracting ? <span className="inline-flex items-center gap-1.5 text-primary"><Loader2 className="w-3 h-3 animate-spin" /> Reading figures from PDF…</span> : 'or drag & drop the Sage PDF here — figures & date autofill'}
              </span>
              {reportUrl && (
                <a href={reportUrl} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()}
                  className="ml-auto inline-flex items-center gap-1.5 text-green-400 hover:text-green-300 text-[11px] truncate max-w-[240px]">
                  <Download className="w-3.5 h-3.5" /> {reportName || 'View report'}
                </a>
              )}
            </div>
          </div>
          <div>
            <label className="text-white/40 text-[10px] font-bold tracking-widest mb-1.5 block">NOTES (OPTIONAL)</label>
            <input
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className="seo-input"
              placeholder="e.g. period adjustment, reason for out-of-balance save"
            />
          </div>
        </div>

        {/* Result */}
        {!hasDate ? (
          <div className="flex items-center gap-2 text-white/30 text-xs border-t border-white/5 pt-3">
            <Lock className="w-3.5 h-3.5" /> Enter a cut-off date to see the MRIWS totals for that period.
          </div>
        ) : !base44Totals || base44Totals.count === 0 ? (
          <div className="flex items-center gap-2 text-white/30 text-xs border-t border-white/5 pt-3">
            <AlertTriangle className="w-3.5 h-3.5" /> No audited invoices fall on or before {reconcileDate}.
          </div>
        ) : (
          <div className="border-t border-white/5 pt-4 space-y-3">
            {hasSage && (
              <div className={`flex items-center gap-2 px-3 py-2 border ${balanced ? 'border-green-400/40 bg-green-400/[0.06] text-green-400' : 'border-red-400/40 bg-red-400/[0.06] text-red-400'}`}>
                {balanced ? <CheckCircle2 className="w-4 h-4" /> : <AlertTriangle className="w-4 h-4" />}
                <span className="text-[11px] font-bold tracking-widest">
                  {balanced ? 'BALANCED — MRIWS MATCHES SAGE' : 'OUT OF BALANCE — SEE DIFFERENCES BELOW'}
                </span>
              </div>
            )}
            <div className="grid grid-cols-4 gap-3 text-xs">
              <div className="space-y-1">
                <p className="text-white/30 text-[10px] font-bold tracking-widest">LAST RECONCILIATION</p>
                <div className="space-y-0.5">
                  <p className="text-white/50 text-[10px]">Sage: <span className="text-white font-bold">{lastRecon ? fmt(lastRecon.sage_gross) : '—'}</span></p>
                  <p className="text-white/50 text-[10px]">MRIWS: <span className="text-white font-bold">{lastRecon ? fmt(lastRecon.base44_gross) : '—'}</span></p>
                  <p className="text-white/30 text-[9px]">{lastRecon ? `saved ${parseAnyDate(lastRecon.reconcile_date)?.toLocaleDateString('en-GB') || lastRecon.reconcile_date}` : 'no prior recon'}</p>
                </div>
              </div>
              <Row label="NET" value={hasSage ? sage.net : null} base44={base44Totals.net} diff={diff?.net} />
              <Row label="VAT" value={hasSage ? sage.vat : null} base44={base44Totals.vat} diff={diff?.vat} />
              <Row label="GROSS" value={hasSage ? sage.gross : null} base44={base44Totals.gross} diff={diff?.gross} />
            </div>
            <div className="flex items-center gap-3 flex-wrap">
              <p className="text-white/30 text-[10px] flex-1 min-w-[200px]">
                MRIWS total computed from <span className="text-white/60 font-bold">{base44Totals.count}</span> audited document{base44Totals.count === 1 ? '' : 's'} dated on or before {reconcileDate}.
              </p>
              <button
                onClick={approveAndSave}
                disabled={!canSave}
                title={!hasSage ? 'Enter all three Sage figures' : !base44Totals?.count ? 'No documents in period' : 'Approve and save this reconciliation to the history below'}
                className="inline-flex items-center gap-1.5 bg-primary hover:bg-primary/90 disabled:opacity-40 disabled:cursor-not-allowed text-white font-bold text-[10px] tracking-widest px-4 py-2 transition-colors"
              >
                {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />} APPROVE &amp; SAVE RECONCILIATION
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Saved reconciliation history */}
      <div className="border border-white/10 bg-[#111115] p-5 space-y-3">
        <div className="flex items-center gap-2">
          <History className="w-4 h-4 text-primary" />
          <p className="text-primary text-[11px] font-bold tracking-widest">APPROVED RECONCILIATIONS</p>
          <span className="text-white/30 text-[10px] ml-auto">{history.length} on record</span>
        </div>

        {loadingHistory ? (
          <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-primary" /></div>
        ) : history.length === 0 ? (
          <div className="text-center py-6 text-white/30">
            <CheckCircle2 className="w-8 h-8 mx-auto mb-2 opacity-30" />
            <p className="text-[11px] font-bold tracking-widest">NO RECONCILIATIONS SAVED YET</p>
            <p className="text-[10px] mt-1">Approved reconciliations appear here for audit history.</p>
          </div>
        ) : (
          <div className="border border-white/10 divide-y divide-white/5 overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="bg-[#0d0d0f] text-white/40">
                  <th className="text-left font-bold py-2 px-3">CUT-OFF</th>
                  <th className="text-right font-bold py-2 px-3">DOCS</th>
                  <th className="text-right font-bold py-2 px-3">MRIWS GROSS</th>
                  <th className="text-right font-bold py-2 px-3">SAGE GROSS</th>
                  <th className="text-right font-bold py-2 px-3">DIFF</th>
                  <th className="text-center font-bold py-2 px-3">STATUS</th>
                  <th className="text-left font-bold py-2 px-3">APPROVED</th>
                  <th className="text-center font-bold py-2 px-3">REPORT</th>
                  <th className="text-center font-bold py-2 px-3"></th>
                </tr>
              </thead>
              <tbody>
                {history.map((r) => {
                  const d = parseAnyDate(r.reconcile_date);
                  return (
                    <tr key={r.id} className="hover:bg-white/[0.02] transition-colors">
                      <td className="py-2 px-3 text-white font-mono">{d ? d.toLocaleDateString('en-GB') : r.reconcile_date}</td>
                      <td className="py-2 px-3 text-right text-white/60">{r.document_count ?? '—'}</td>
                      <td className="py-2 px-3 text-right text-white/70">{fmt(r.base44_gross)}</td>
                      <td className="py-2 px-3 text-right text-white">{fmt(r.sage_gross)}</td>
                      <td className={`py-2 px-3 text-right font-bold ${(Number(r.diff_gross) || 0) === 0 ? 'text-green-400' : 'text-red-400'}`}>
                        {(Number(r.diff_gross) || 0) > 0 ? '+' : ''}{fmt(r.diff_gross)}
                      </td>
                      <td className="py-2 px-3 text-center">
                        {r.balanced ? (
                          <span className="inline-flex items-center gap-1 text-[9px] font-bold tracking-widest border border-green-400/30 text-green-400 px-1.5 py-0.5">
                            <CheckCircle2 className="w-3 h-3" /> BALANCED
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-[9px] font-bold tracking-widest border border-red-400/30 text-red-400 px-1.5 py-0.5">
                            <AlertTriangle className="w-3 h-3" /> OUT OF BALANCE
                          </span>
                        )}
                      </td>
                      <td className="py-2 px-3 text-white/50">
                        <div className="text-[10px]">{r.approved_by || '—'}</div>
                        {r.approved_at && (
                          <div className="text-[9px] text-white/30">{new Date(r.approved_at).toLocaleString('en-GB')}</div>
                        )}
                      </td>
                      <td className="py-2 px-3 text-center">
                        {r.sage_report_url ? (
                          <a href={r.sage_report_url} target="_blank" rel="noreferrer" title="Open Sage report PDF"
                            className="inline-flex items-center justify-center w-7 h-7 border border-orange-400/30 bg-orange-400/10 hover:bg-orange-400 hover:text-black text-orange-400 transition-colors">
                            <FileText className="w-3.5 h-3.5" />
                          </a>
                        ) : <span className="text-white/20">—</span>}
                      </td>
                      <td className="py-2 px-3 text-center">
                        <button onClick={() => deleteHistory(r.id)} title="Delete reconciliation"
                          className="inline-flex items-center justify-center w-7 h-7 border border-red-400/30 bg-red-400/10 hover:bg-red-400 hover:text-white text-red-400 transition-colors">
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// Generate an audit PDF for an approved reconciliation showing the figures
// and details, then trigger a download. Used for record-keeping after a
// reconciliation is locked.
function downloadReconciliationPdf(rec) {
  const doc = new jsPDF({ unit: 'pt', format: 'a4' });
  const W = doc.internal.pageSize.getWidth();
  const M = 48;
  const ACCENT = { r: 221, g: 31, b: 26 };
  let y = 56;

  // Header band
  doc.setFillColor(18, 18, 21); doc.rect(0, 0, W, 84, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFont('helvetica', 'bold'); doc.setFontSize(16);
  doc.text('Sage Reconciliation — Audit Report', M, 40);
  doc.setFont('helvetica', 'normal'); doc.setFontSize(9);
  doc.setTextColor(180, 180, 185);
  doc.text('Motion Reality Sim Racing Ltd', M, 60);
  doc.text(`Generated ${new Date().toLocaleString('en-GB')}`, M, 74);
  y = 110;

  // Status badge
  const balanced = !!rec.balanced;
  doc.setFillColor(balanced ? 74 : 248, balanced ? 222 : 113, balanced ? 128 : 113);
  doc.roundedRect(M, y, 150, 22, 3, 3, 'F');
  doc.setTextColor(18, 18, 21); doc.setFont('helvetica', 'bold'); doc.setFontSize(9);
  doc.text(balanced ? 'BALANCED' : 'OUT OF BALANCE', M + 10, y + 15);
  y += 40;

  // Reference line
  doc.setTextColor(110, 110, 115); doc.setFont('helvetica', 'normal'); doc.setFontSize(8);
  doc.text(`Reference: ${rec.id || '—'}`, M, y);
  y += 18;

  // Details table
  const rows = [
    ['Cut-off date', rec.reconcile_date || '—'],
    ['Documents counted', String(rec.document_count ?? '—')],
    ['Approved by', rec.approved_by || '—'],
    ['Approved at', rec.approved_at ? new Date(rec.approved_at).toLocaleString('en-GB') : '—'],
  ];
  doc.setFontSize(9);
  for (const [k, v] of rows) {
    doc.setTextColor(120, 120, 125); doc.text(k, M, y);
    doc.setTextColor(20, 20, 24); doc.text(String(v), M + 160, y);
    y += 16;
  }
  y += 10;

  // Figures table
  doc.setFillColor(ACCENT.r, ACCENT.g, ACCENT.b);
  doc.rect(M, y, W - M * 2, 20, 'F');
  doc.setTextColor(255, 255, 255); doc.setFont('helvetica', 'bold'); doc.setFontSize(9);
  doc.text('FIGURES (GBP)', M + 8, y + 14);
  y += 20;

  const cols = ['', 'Net', 'VAT', 'Gross'];
  const colX = [M, M + 150, M + 270, M + 390];
  doc.setFillColor(245, 245, 247); doc.rect(M, y, W - M * 2, 18, 'F');
  doc.setTextColor(90, 90, 95); doc.setFont('helvetica', 'bold'); doc.setFontSize(8);
  cols.forEach((c, i) => doc.text(c, colX[i] + 8, y + 12));
  y += 18;

  const figRows = [
    ['MRIWS', rec.base44_net, rec.base44_vat, rec.base44_gross],
    ['Sage', rec.sage_net, rec.sage_vat, rec.sage_gross],
    ['Difference', rec.diff_net, rec.diff_vat, rec.diff_gross],
  ];
  doc.setFont('helvetica', 'normal'); doc.setFontSize(9);
  figRows.forEach((r, i) => {
    if (i % 2 === 0) { doc.setFillColor(250, 250, 251); doc.rect(M, y, W - M * 2, 16, 'F'); }
    doc.setTextColor(i === 2 ? 120 : 20, i === 2 ? 120 : 20, i === 2 ? 125 : 24);
    doc.setFont('helvetica', i === 2 ? 'bold' : 'normal');
    doc.text(String(r[0]), colX[0] + 8, y + 11);
    [1, 2, 3].forEach((j) => doc.text(fmt(r[j]), colX[j] + 8, y + 11));
    y += 16;
  });
  y += 16;

  // Last reconciliation reference
  if (rec.last_recon) {
    const lr = rec.last_recon;
    doc.setTextColor(120, 120, 125); doc.setFont('helvetica', 'normal'); doc.setFontSize(8);
    doc.text(`Previous reconciliation: cut-off ${lr.reconcile_date || '—'} · Sage gross ${fmt(lr.sage_gross)} · ${lr.balanced ? 'balanced' : 'out of balance'}`, M, y);
    y += 16;
  }

  // Notes
  if (rec.notes) {
    doc.setTextColor(120, 120, 125); doc.setFont('helvetica', 'bold'); doc.setFontSize(8);
    doc.text('NOTES', M, y); y += 14;
    doc.setFont('helvetica', 'normal'); doc.setTextColor(40, 40, 45);
    const lines = doc.splitTextToSize(String(rec.notes), W - M * 2);
    doc.text(lines, M, y);
    y += lines.length * 12 + 8;
  }

  // Footer
  doc.setDrawColor(225, 225, 228); doc.line(M, 760, W - M, 760);
  doc.setFontSize(7); doc.setTextColor(150, 150, 155);
  doc.text('Motion Reality Sim Racing Ltd — Sage Reconciliation Audit Report', M, 780);
  doc.text('This document is computer-generated for audit purposes.', M, 792);

  doc.save(`reconciliation-${rec.reconcile_date || 'audit'}.pdf`);
}

function Row({ label, value, base44, diff }) {
  const showDiff = diff !== null && diff !== undefined && diff !== 0;
  return (
    <div className="space-y-1">
      <p className="text-white/30 text-[10px] font-bold tracking-widest">{label}</p>
      <div className="space-y-0.5">
        <p className="text-white/50 text-[10px]">Sage: <span className="text-white font-bold">{value !== null ? fmt(value) : '—'}</span></p>
        <p className="text-white/50 text-[10px]">MRIWS: <span className="text-white font-bold">{base44 !== undefined ? fmt(base44) : '—'}</span></p>
        {showDiff && (
          <p className={`text-[10px] font-bold ${diff > 0 ? 'text-red-400' : 'text-orange-400'}`}>
            diff: {diff > 0 ? '+' : ''}{fmt(diff)}
          </p>
        )}
      </div>
    </div>
  );
}